import { GRID_COLUMNS, createSectionColumnId } from '../utils/proposalSectionColumns';
import { DEFAULT_BORDER } from '../../shared/boxes/borderStyles';
import { infoCardDefaultProps } from '../../shared/boxes/infoCard/infoCardProps';
import { logoStripCarouselProps } from '../blocks/web/CarouselBlock/carouselDefaults';
import { SECTION_LAYOUT_PRESETS, presetFullMainSpans } from '../utils/proposalSectionPresets';
import {
  COMPOSITION_BOX,
  compositionsByIds,
} from '../../shared/compositions/compositionCatalog';

/**
 * Ce que pose chaque tuile de l'onglet Blocs : une disposition décrite, que
 * `buildSectionFromComposition` transforme en Bloc.
 *
 * Une seule description par tuile, et elle sert deux fois — au drop pour poser
 * le Bloc, dans la palette pour en dessiner le croquis. Les décrire deux fois
 * était la garantie qu'un jour la vignette montrerait autre chose que ce que le
 * drop pose.
 *
 * Deux familles y cohabitent :
 *  - les **macros**, qui posent du contenu de démonstration (bannières,
 *    chiffres clés, questions/réponses…) — l'utilisateur remplace ;
 *  - les **compositions**, qui posent des box VIDES et viennent du catalogue
 *    partagé avec l'éditeur classique (`src/shared/compositions/`).
 */

/* ── Box posées dans les cellules ─────────────────────────────────────────── */

const emptyText = () => ({
  type: 'ProposalRichText',
  props: { id: `text-${createSectionColumnId()}`, html: '', isVisible: true },
});

const emptyTitle = (titleLevel) => ({
  type: 'ProposalTitle',
  props: {
    id: `title-${createSectionColumnId()}`, html: '', titleLevel, isVisible: true,
  },
});

const emptyImage = () => ({
  type: 'ProposalImage',
  props: {
    id: `image-${createSectionColumnId()}`, src: '', alt: '', alignment: 'center',
  },
});

const emptyVideo = () => ({
  type: 'VideoBlock',
  props: { id: `video-${createSectionColumnId()}`, src: '' },
});

/**
 * Le bouton d'une macro de bandeau. Les trois bandeaux posaient les mêmes dix
 * lignes, recopiées : blanc sur fond bleu, centré, sans icône. Le libellé porte
 * son propre sombre — Mantine ne recalcule pas la couleur du texte sur une
 * couleur libre (cf. HERO_BUTTON_LABEL_HTML).
 */
function macroButtonBox(labelHtml) {
  return {
    type: 'ButtonBlock',
    props: {
      id: `button-${createSectionColumnId()}`,
      label: labelHtml,
      url: '',
      color: '#ffffff',
      size: 'md',
      radius: 'xl',
      icon: '',
      iconColor: '#ffffff',
      alignHorizontal: 'center',
      alignVertical: 'center',
      isVisible: true,
    },
  };
}

/** Une ligne d'une seule cellule, portant cette box. */
const stackedRow = (box) => ({ columns: [{ span: GRID_COLUMNS, box }] });

/* ── Macros à contenu de démonstration ────────────────────────────────────── */

const logoStripLayout = () => ({
  slug: 'logostrip',
  columns: [{
    span: GRID_COLUMNS,
    box: {
      type: 'CarouselBlock',
      props: { ...logoStripCarouselProps(), id: `logos-${createSectionColumnId()}` },
    },
  }],
});

/**
 * Cards démo de la macro « Chiffres clés » : 3 InfoCards distinctes pour que
 * l'utilisateur voie le rendu et remplace par ses données.
 */
const KEY_FIGURES_DEMO = [
  { icon: 'IconUsers', value: '+120', description: 'Clients accompagnés' },
  { icon: 'IconThumbUp', value: '98 %', description: 'Clients satisfaits' },
  { icon: 'IconClock', value: '24/7', description: 'Support disponible' },
];

const keyFiguresLayout = () => ({
  slug: 'keyfigures',
  columns: KEY_FIGURES_DEMO.map((demo) => ({
    span: Math.floor(GRID_COLUMNS / KEY_FIGURES_DEMO.length),
    box: {
      type: 'InfoCard',
      props: {
        ...infoCardDefaultProps(),
        id: `info-${createSectionColumnId()}`,
        icon: demo.icon,
        value: demo.value,
        description: demo.description,
      },
    },
  })),
});

/**
 * Volets démo de la macro « Questions / réponses » : 3 Q/R distinctes pour
 * que l'utilisateur voie le rendu et remplace par ses données.
 */
const FAQ_DEMO = [
  {
    question: 'Quels sont vos délais ?',
    answer: '<p>Nous livrons généralement sous 2 à 4 semaines selon le périmètre du projet.</p>',
  },
  {
    question: 'Comment se déroule un projet ?',
    answer: '<p>Un point de cadrage, des livraisons régulières, et un interlocuteur dédié du début à la fin.</p>',
  },
  {
    question: 'Proposez-vous un accompagnement après livraison ?',
    answer: '<p>Oui — support, maintenance et évolutions sont couverts par nos offres de suivi.</p>',
  },
];

const faqLayout = () => ({
  slug: 'faq',
  columns: [{
    span: GRID_COLUMNS,
    rows: FAQ_DEMO.map((demo) => stackedRow({
      type: 'AccordionPanel',
      props: {
        id: `panel-${createSectionColumnId()}`,
        question: demo.question,
        answer: demo.answer,
        panelColor: '#f8f9fa',
        answerBackground: '#ffffff',
        border: { ...DEFAULT_BORDER },
        openBehavior: 'exclusive',
      },
    })),
  }],
});

/**
 * Contenu démo de la macro « Bannière d'accueil » : titre, phrase d'accroche et
 * bouton, tous centrés. Le centrage est écrit en `text-align` inline — c'est ce
 * que produit l'extension TipTap TextAlign, donc la barre d'outils du canvas
 * reprend la main dessus sans conversion.
 */
const HERO_TITLE_HTML = '<h2 style="text-align: center"><span style="color: #ffffff"><strong>Donnons vie à votre projet</strong></span></h2>';
const HERO_TEXT_HTML = '<p style="text-align: center"><span style="color: #ffffff">Une équipe, une méthode et des livraisons régulières, du cadrage à la mise en ligne.</span></p>';
const HERO_BUTTON_LABEL_HTML = '<p><span style="color: #212529">Démarrer le projet</span></p>';

const heroLayout = () => ({
  slug: 'hero',
  sectionProps: {
    // Bleu de l'ancienne bannière : le titre et la description sont posés en
    // blanc pour aller avec (cf. HERO_TITLE_HTML / HERO_TEXT_HTML).
    backgroundColor: '#228be6',
    paddingTop: '3rem',
    paddingBottom: '3rem',
  },
  columns: [{
    span: GRID_COLUMNS,
    rows: [
      stackedRow({
        type: 'ProposalTitle',
        props: {
          id: `title-${createSectionColumnId()}`,
          html: HERO_TITLE_HTML,
          titleLevel: 2,
          isVisible: true,
        },
      }),
      stackedRow({
        type: 'ProposalRichText',
        props: {
          id: `text-${createSectionColumnId()}`,
          html: HERO_TEXT_HTML,
          isVisible: true,
        },
      }),
      stackedRow(macroButtonBox(HERO_BUTTON_LABEL_HTML)),
    ],
  }],
});

/**
 * Contenu démo de la macro « Bannière d'action » : titre et accroche à gauche,
 * bouton à droite — la disposition du bandeau qu'elle remplace.
 */
const ACTION_TITLE_HTML = '<h3><span style="color: #ffffff"><strong>Prêt à lancer votre projet ?</strong></span></h3>';
const ACTION_TEXT_HTML = '<p><span style="color: #ffffff">Un échange de 30 minutes suffit pour cadrer vos besoins et vous donner une première estimation.</span></p>';
const ACTION_BUTTON_LABEL_HTML = '<p><span style="color: #212529">Prendre rendez-vous</span></p>';

const actionBannerLayout = () => ({
  slug: 'action',
  sectionProps: {
    backgroundColor: '#228be6',
    paddingTop: '2rem',
    paddingBottom: '2rem',
  },
  // Le bouton passe sous le texte en dessous de `md` : côte à côte, il serait
  // écrasé sur un écran étroit.
  columns: [
    {
      span: 8,
      rows: [
        stackedRow({
          type: 'ProposalTitle',
          props: {
            id: `title-${createSectionColumnId()}`,
            html: ACTION_TITLE_HTML,
            titleLevel: 3,
            isVisible: true,
          },
        }),
        stackedRow({
          type: 'ProposalRichText',
          props: {
            id: `text-${createSectionColumnId()}`,
            html: ACTION_TEXT_HTML,
            isVisible: true,
          },
        }),
      ],
    },
    { span: 4, rows: [stackedRow(macroButtonBox(ACTION_BUTTON_LABEL_HTML))] },
  ],
});

/* ── Compositions du catalogue partagé (box vides) ────────────────────────── */

/** Vocabulaire neutre du catalogue -> box du site, vide de tout contenu. */
const EMPTY_WEB_BOXES = {
  [COMPOSITION_BOX.TEXT]: emptyText,
  [COMPOSITION_BOX.TITLE_2]: () => emptyTitle(2),
  [COMPOSITION_BOX.TITLE_3]: () => emptyTitle(3),
  [COMPOSITION_BOX.IMAGE]: emptyImage,
  [COMPOSITION_BOX.VIDEO]: emptyVideo,
};

const toWebColumn = (column) => ({
  span: column.span,
  ...(column.rows
    ? { rows: column.rows.map((row) => ({ columns: row.columns.map(toWebColumn) })) }
    : {}),
  ...(column.box ? { box: EMPTY_WEB_BOXES[column.box]() } : {}),
});

/**
 * Compositions reprises du catalogue partagé, et le type de tuile qui les pose.
 *
 * Les quatre premières remplacent les macros « Image + Texte » et
 * « Vidéo + Texte », qui ne disaient pas de quel côté tombait le média — et
 * n'en proposaient qu'un seul.
 *
 * Le catalogue partagé en porte trois de plus — « Trois cartes illustrées »,
 * « Deux colonnes titrées » et « Titre + deux colonnes » — que le site ne
 * propose pas : elles se composent ici en posant une grille puis ses box.
 * Elles restent au document, où poser une grille demande plus de gestes.
 */
const SHARED_COMPOSITION_TYPES = {
  'media-left-text-right': 'ImageLeftTextBlock',
  'text-left-media-right': 'TextImageRightBlock',
  'video-left-text-right': 'VideoLeftTextBlock',
  'text-left-video-right': 'TextVideoRightBlock',
  'text-with-stacked-media': 'TextMediaCaptionBlock',
  'banner-image-with-caption': 'ImageCaptionBlock',
};

/** Les compositions partagées, dans l'ordre où la palette les montre. */
export const SHARED_COMPOSITIONS = compositionsByIds(Object.keys(SHARED_COMPOSITION_TYPES))
  .map((composition) => ({
    ...composition,
    type: SHARED_COMPOSITION_TYPES[composition.id],
  }));

const sharedCompositionLayouts = Object.fromEntries(
  SHARED_COMPOSITIONS.map(({ id, type, columns }) => [
    type,
    () => ({ slug: id, columns: columns.map(toWebColumn) }),
  ]),
);

/* ── Grilles vides ────────────────────────────────────────────────────────── */

/**
 * Les mêmes grilles que le sélecteur de disposition d'un Bloc
 * (`SECTION_LAYOUT_PRESETS`) : poser une grille vide depuis la palette et
 * réorganiser un Bloc existant doivent proposer le même jeu.
 *
 * « 1 colonne » et « 2 colonnes » n'y sont pas : à la racine, déposer une box
 * crée déjà son Bloc d'une colonne, et deux moitiés se composent en un geste —
 * une vignette pour ça prenait de la place sans rien faire gagner. Même
 * arbitrage que l'éditeur classique.
 */
const TRIVIAL_GRID_PRESETS = ['one', 'two-equal'];

const gridPresetType = (key) => `Grid:${key}`;

export const GRID_PRESETS = SECTION_LAYOUT_PRESETS
  .filter((def) => !TRIVIAL_GRID_PRESETS.includes(def.key))
  .map((def) => ({ ...def, type: gridPresetType(def.key) }));

/** Un préréglage de disposition → colonnes vides, sans aucune box. */
const gridLayout = (def) => () => ({
  slug: `grid-${def.key}`,
  columns: def.row
    ? def.row.map((span) => ({ span }))
    : def.mains.map((rows, mainIdx) => ({
      span: presetFullMainSpans(def)[mainIdx],
      rows: rows.map((spans) => ({ columns: spans.map((span) => ({ span })) })),
    })),
});

const gridLayouts = Object.fromEntries(
  GRID_PRESETS.map((def) => [def.type, gridLayout(def)]),
);

/* ── Registre ─────────────────────────────────────────────────────────────── */

/**
 * Type de tuile → fabrique de disposition. Une fabrique rend des identifiants
 * neufs à chaque appel : on l'appelle une fois par drop.
 */
export const COMPOSITION_LAYOUTS = {
  ...gridLayouts,
  HeroBlock: heroLayout,
  ActionBannerBlock: actionBannerLayout,
  KeyFiguresBlock: keyFiguresLayout,
  FaqBlock: faqLayout,
  LogoStripBlock: logoStripLayout,
  ...sharedCompositionLayouts,
};

/** Types de tuile posant un Bloc, dans l'ordre d'affichage de la palette. */
export const COMPOSITION_TILE_TYPES = Object.keys(COMPOSITION_LAYOUTS);

const sketchColumnsCache = {};

/**
 * Les colonnes à dessiner dans la vignette de cette tuile, ou `null` si elle
 * n'en pose pas. Mémoïsé : la structure ne change jamais, et une fabrique
 * génère des identifiants à chaque appel — inutile à chaque rendu de palette.
 */
export function compositionSketchColumns(type) {
  const layout = COMPOSITION_LAYOUTS[type];
  if (!layout) return null;
  if (!sketchColumnsCache[type]) sketchColumnsCache[type] = layout().columns;
  return sketchColumnsCache[type];
}
